/**
 * SafetyKPICard — TRIR / DART / incident-frequency dashboard for the
 * Reports page. Backed by /api/reports/safety-kpi.
 *
 * TRIR = (recordable injuries × 200,000) / hours-worked.
 * DART = (recordable cases with days_away OR days_restricted × 200,000) / hours-worked.
 *
 * Both numbers are only useful next to context, so we render:
 *   1. The two big numbers with year switcher.
 *   2. Recordable count + DART case count in the KPI strip.
 *   3. A 12-month incident-frequency stacked bar chart across ALL
 *      types (injury/near-miss/stop-work/property-damage) so the
 *      reader sees near-miss volume trending alongside injury data.
 *   4. Admin-only hours-worked editor (per-year override) so the
 *      denominator can be tuned as headcount changes.
 *   5. OSHA 300 CSV export button (admin/pm only) for the same year.
 */
import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, HeartPulse, ChevronLeft, ChevronRight, Download, Save } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const TYPE_COLORS = {
  injury: "#EF4444",
  near_miss: "#F59E0B",
  stop_work: "#8B5CF6",
  property_damage: "#3B82F6",
};

function currentYear() { return new Date().getFullYear(); }
function fmtMonth(key) {
  // "2026-01" → "Jan"
  const m = key?.split("-")?.[1];
  return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(m) - 1] || key;
}

// Industry safety benchmarks (US construction, 2020s):
//   TRIR: <2 excellent · 2–3 average · >3 needs work
//   DART: <1 excellent · 1–2 average · >2 needs work
function tone(trir) {
  if (trir === null || trir === undefined) return "text-slate-300";
  if (trir < 2) return "text-emerald-400";
  if (trir < 3) return "text-amber-400";
  return "text-red-400";
}

export default function SafetyKPICard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAdminOrPM = user?.role === "admin" || user?.role === "pm";
  const [year, setYear] = useState(currentYear());
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [hoursDraft, setHoursDraft] = useState("");
  const [savingHours, setSavingHours] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/reports/safety-kpi?year=${year}`).then((r) => {
      setData(r.data);
      setHoursDraft(String(r.data.hours_worked || ""));
    }).catch(() => setData(null));
  }, [year]);

  const saveHours = async () => {
    if (!isAdmin) return;
    const n = Number(hoursDraft);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a positive number of hours.");
      return;
    }
    setSavingHours(true);
    try {
      await api.post("/reports/safety-hours", {
        hours_by_year: { [String(year)]: n },
      });
      toast.success(`Hours-worked for ${year} saved.`);
      setEditing(false);
      // Re-fetch so TRIR/DART reflect the new denominator.
      const r = await api.get(`/reports/safety-kpi?year=${year}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSavingHours(false);
    }
  };

  const downloadOsha = () => {
    if (!isAdminOrPM) return;
    window.location.href = `${API_BASE}/reports/osha-300.csv?year=${year}`;
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="safety-kpi-card">
      <CardContent className="p-5">
        {/* Header + year switcher */}
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-2 rounded-md border border-red-900 bg-red-950/40 text-red-300 shrink-0">
              <Activity className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">
                Safety Performance
              </div>
              <div className="font-display font-bold text-lg tracking-tight">
                TRIR &amp; DART · {year}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => setYear(year - 1)}
              className="border-slate-700 bg-slate-950 hover:bg-slate-800 h-8 px-2" data-testid="safety-kpi-prev-year">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-mono text-sm px-2" data-testid="safety-kpi-year-label">{year}</span>
            <Button size="sm" variant="outline" onClick={() => setYear(year + 1)}
              disabled={year >= currentYear()}
              className="border-slate-700 bg-slate-950 hover:bg-slate-800 h-8 px-2 disabled:opacity-40" data-testid="safety-kpi-next-year">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {data === null ? (
          <div className="text-slate-500 text-sm py-8 text-center" data-testid="safety-kpi-loading">Loading…</div>
        ) : (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <KPI label="TRIR" value={data.trir?.toFixed(2)} tone={tone(data.trir)} testid="safety-kpi-trir" />
              <KPI label="DART" value={data.dart?.toFixed(2)} tone={tone(data.dart)} testid="safety-kpi-dart" />
              <KPI label="Recordable" value={data.recordable_count} testid="safety-kpi-recordable" />
              <KPI label="DART Cases" value={data.dart_count} testid="safety-kpi-dart-cases" />
            </div>

            {/* Hours worked strip */}
            <div className="flex items-center justify-between gap-2 mb-4 p-2.5 rounded-md border border-slate-800 bg-slate-950" data-testid="safety-kpi-hours-strip">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">Hours worked</span>
                {editing && isAdmin ? (
                  <>
                    <Input
                      type="number" min="0" step="1000"
                      value={hoursDraft}
                      onChange={(e) => setHoursDraft(e.target.value)}
                      className="w-28 h-7 text-xs bg-slate-900 border-slate-700"
                      data-testid="safety-kpi-hours-input"
                    />
                    <Button size="sm" onClick={saveHours} disabled={savingHours} className="h-7 bg-blue-700 hover:bg-blue-600 text-xs" data-testid="safety-kpi-hours-save">
                      <Save className="w-3 h-3 mr-1" /> {savingHours ? "Saving…" : "Save"}
                    </Button>
                    <button type="button" onClick={() => { setEditing(false); setHoursDraft(String(data.hours_worked || "")); }}
                      className="text-[11px] text-slate-400 hover:text-slate-200 underline" data-testid="safety-kpi-hours-cancel">
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="font-mono text-sm text-slate-100" data-testid="safety-kpi-hours-value">
                      {data.hours_worked?.toLocaleString()}
                    </span>
                    {isAdmin && (
                      <button type="button" onClick={() => setEditing(true)}
                        className="text-[11px] text-blue-400 hover:text-blue-200 underline" data-testid="safety-kpi-hours-edit">
                        edit
                      </button>
                    )}
                  </>
                )}
              </div>
              {isAdminOrPM && (
                <Button size="sm" onClick={downloadOsha}
                  className="h-7 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-display tracking-wider"
                  data-testid="safety-kpi-osha-btn">
                  <Download className="w-3 h-3 mr-1" /> OSHA 300 CSV
                </Button>
              )}
            </div>

            {/* Monthly incident chart */}
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold mb-1">
              Incident Frequency by Month
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_month || []} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#0F172A", border: "1px solid #334155", color: "#F1F5F9", fontSize: 12 }}
                  cursor={{ fill: "#1E293B", opacity: 0.4 }}
                  labelFormatter={fmtMonth}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="injury"          name="Injury"          stackId="a" fill={TYPE_COLORS.injury} />
                <Bar dataKey="near_miss"       name="Near Miss/Good Catch" stackId="a" fill={TYPE_COLORS.near_miss} />
                <Bar dataKey="stop_work"       name="Stop-Work"       stackId="a" fill={TYPE_COLORS.stop_work} />
                <Bar dataKey="property_damage" name="Property Damage" stackId="a" fill={TYPE_COLORS.property_damage} />
              </BarChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-slate-500 mt-2 leading-tight">
              TRIR benchmark: &lt;2.0 excellent · 2–3 average · &gt;3 needs improvement. Rates normalize per 200,000 hours worked (100 FTE × 40 hrs × 50 wks).
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function KPI({ label, value, tone, testid }) {
  return (
    <div className="p-3 rounded-md border border-slate-800 bg-slate-950" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tight mt-1 ${tone || "text-slate-100"}`}>{value ?? "—"}</div>
    </div>
  );
}
